// ═══════════════════════════════════════════════════════════════════════════════
// Logo library — preset icons tenants can pick instead of (or before)
// uploading their own. Each entry points at a pair of SVG files in
// /public/logos/ on the B2C app, served at /logos/<slug>.svg and
// /logos/<slug>-dark.svg from every tenant subdomain (same bundle).
//
// Picking a preset writes the file paths to branding_configs.logo_url,
// logo_dark_url, and favicon_url — the existing rendering pipeline
// (TenantContext → BrandBioCluster) handles everything from there.
//
// SVGs all use the same outline-circle visual language as the
// DiscoveryGem mark so a tenant's pick still feels like part of the
// platform's design family. Add entries here as we ship more.
// ═══════════════════════════════════════════════════════════════════════════════

export interface LogoLibraryItem {
  /** Stable slug — also the filename base. */
  slug:        string;
  /** Display name in the picker. */
  name:        string;
  /** One-line vibe blurb. */
  description: string;
  /** Tags so we can group / filter later if the library grows. */
  tags:        string[];
  /** Public path to the light-on-transparent SVG (black strokes — for use on light bg). */
  lightUrl:    string;
  /** Public path to the dark-on-transparent SVG (white strokes — for use on dark bg). */
  darkUrl:     string;
  /** Public path to the THEME-ADAPTIVE favicon SVG. Embeds a
   *  `prefers-color-scheme` media query so the strokes auto-flip
   *  between black and white based on the browser tab strip color.
   *  This is what gets written to branding_configs.favicon_url so
   *  the icon stays visible on Chrome / Safari / Edge (dark tabs)
   *  AND Firefox / light-themed Chrome (light tabs). */
  faviconUrl:  string;
}

export const LOGO_LIBRARY: LogoLibraryItem[] = [
  {
    slug:        'heart-plane',
    name:        'Heart + Plane',
    description: 'A heart pierced by a small paper plane. Romantic, travel-forward — built for couples-and-friends brands.',
    tags:        ['heart', 'plane', 'couples', 'romantic'],
    lightUrl:    '/logos/heart-plane.svg',
    darkUrl:     '/logos/heart-plane-dark.svg',
    faviconUrl:  '/logos/heart-plane-favicon.svg',
  },
  {
    slug:        'paper-plane',
    name:        'Paper Plane',
    description: 'The DiscoveryGem mark — a classic paper plane in motion. Clean, minimal, instantly readable.',
    tags:        ['plane', 'minimal', 'classic'],
    lightUrl:    '/logos/paper-plane.svg',
    darkUrl:     '/logos/paper-plane-dark.svg',
    faviconUrl:  '/logos/paper-plane-favicon.svg',
  },
  {
    slug:        'compass',
    name:        'Compass',
    description: 'A north-pointing compass needle inside a ring. Adventurer / explorer feel.',
    tags:        ['compass', 'adventure', 'explorer'],
    lightUrl:    '/logos/compass.svg',
    darkUrl:     '/logos/compass-dark.svg',
    faviconUrl:  '/logos/compass-favicon.svg',
  },
  {
    slug:        'map-pin',
    name:        'Map Pin',
    description: 'A geo pin with a solid dot inside. Local guides, destination experts, neighborhood travel.',
    tags:        ['pin', 'location', 'local'],
    lightUrl:    '/logos/map-pin.svg',
    darkUrl:     '/logos/map-pin-dark.svg',
    faviconUrl:  '/logos/map-pin-favicon.svg',
  },
  {
    slug:        'globe-arc',
    name:        'Globe + Route',
    description: 'A meridian-lined globe with a dotted arc and two endpoints. International travel, multi-stop trips.',
    tags:        ['globe', 'international', 'route'],
    lightUrl:    '/logos/globe-arc.svg',
    darkUrl:     '/logos/globe-arc-dark.svg',
    faviconUrl:  '/logos/globe-arc-favicon.svg',
  },
  {
    slug:        'suitcase',
    name:        'Suitcase',
    description: 'A classic suitcase with handle + center seam. Packing, family travel, baggage-first brands.',
    tags:        ['suitcase', 'family', 'luggage'],
    lightUrl:    '/logos/suitcase.svg',
    darkUrl:     '/logos/suitcase-dark.svg',
    faviconUrl:  '/logos/suitcase-favicon.svg',
  },
  {
    slug:        'mountain',
    name:        'Mountain + Sun',
    description: 'Twin peaks with a sun rising over the shoulder. Hiking, eco, outdoor adventure tourism.',
    tags:        ['mountain', 'outdoor', 'eco', 'adventure'],
    lightUrl:    '/logos/mountain.svg',
    darkUrl:     '/logos/mountain-dark.svg',
    faviconUrl:  '/logos/mountain-favicon.svg',
  },
  {
    slug:        'route',
    name:        'Winding Route',
    description: 'A dotted journey line from a starting dot to a destination arrow. Itinerary-first, planning brands.',
    tags:        ['route', 'itinerary', 'planning'],
    lightUrl:    '/logos/route.svg',
    darkUrl:     '/logos/route-dark.svg',
    faviconUrl:  '/logos/route-favicon.svg',
  },
  {
    slug:        'palm-sun',
    name:        'Palm + Sun',
    description: 'A palm tree under a sun. Beach, tropical, island-getaway brands.',
    tags:        ['beach', 'tropical', 'island'],
    lightUrl:    '/logos/palm-sun.svg',
    darkUrl:     '/logos/palm-sun-dark.svg',
    faviconUrl:  '/logos/palm-sun-favicon.svg',
  },
  {
    slug:        'waves',
    name:        'Waves',
    description: 'Three rolling waves. Coastal cruises, sailing, sea-side properties.',
    tags:        ['waves', 'sea', 'cruise', 'sailing'],
    lightUrl:    '/logos/waves.svg',
    darkUrl:     '/logos/waves-dark.svg',
    faviconUrl:  '/logos/waves-favicon.svg',
  },
  {
    slug:        'star-route',
    name:        'Star',
    description: 'A classic five-pointed star outline. Premium, curated, "must-see" brand positioning.',
    tags:        ['star', 'premium', 'curated'],
    lightUrl:    '/logos/star-route.svg',
    darkUrl:     '/logos/star-route-dark.svg',
    faviconUrl:  '/logos/star-route-favicon.svg',
  },
  {
    slug:        'binoculars',
    name:        'Binoculars',
    description: 'Two lensed binoculars in elevation. Discovery, scouting, "look ahead" brand vibes.',
    tags:        ['binoculars', 'discovery', 'scouting'],
    lightUrl:    '/logos/binoculars.svg',
    darkUrl:     '/logos/binoculars-dark.svg',
    faviconUrl:  '/logos/binoculars-favicon.svg',
  },

  // ── Paper-Plane-in-Circle family ──────────────────────────────────────────
  // Six full-colour treatments of the same mark (thin ring + flat
  // 2-triangle paper plane). Unlike the outline icons above, these
  // are self-contained rounded-square app-icons: the SVG carries its
  // own gradient background, so the same file works on light + dark
  // surfaces. lightUrl / darkUrl / faviconUrl therefore all point at
  // the same file. Generated from /logo-download.html, which lets
  // operators preview + export 1024×1024 PNG/JPG of each variant for
  // app-store / social submissions.
  {
    slug:        'paper-plane-icon',
    name:        'Plane (Icon)',
    description: 'Minimal flat icon — slate plane in a thin slate ring on a clean white square. The neutral-est option.',
    tags:        ['plane', 'minimal', 'icon', 'circle', 'flat', 'paper-plane-family'],
    lightUrl:    '/logos/paper-plane-icon.svg',
    darkUrl:     '/logos/paper-plane-icon.svg',
    faviconUrl:  '/logos/paper-plane-icon.svg',
  },
  {
    slug:        'paper-plane-aurora',
    name:        'Plane (Aurora)',
    description: 'White plane + ring on a deep indigo→purple gradient. Polished, "night-sky" travel feel.',
    tags:        ['plane', 'aurora', 'indigo', 'purple', 'circle', 'paper-plane-family'],
    lightUrl:    '/logos/paper-plane-aurora.svg',
    darkUrl:     '/logos/paper-plane-aurora.svg',
    faviconUrl:  '/logos/paper-plane-aurora.svg',
  },
  {
    slug:        'paper-plane-midnight',
    name:        'Plane (Midnight)',
    description: 'White plane + ring on a near-black gradient. Maximum-contrast minimal — reads sharp at every size.',
    tags:        ['plane', 'midnight', 'black', 'minimal', 'circle', 'paper-plane-family'],
    lightUrl:    '/logos/paper-plane-midnight.svg',
    darkUrl:     '/logos/paper-plane-midnight.svg',
    faviconUrl:  '/logos/paper-plane-midnight.svg',
  },
  {
    slug:        'paper-plane-sunset',
    name:        'Plane (Sunset)',
    description: 'Cream plane + ring on a burnt-orange→magenta gradient. Warm, golden-hour, tropical travel.',
    tags:        ['plane', 'sunset', 'orange', 'warm', 'circle', 'paper-plane-family'],
    lightUrl:    '/logos/paper-plane-sunset.svg',
    darkUrl:     '/logos/paper-plane-sunset.svg',
    faviconUrl:  '/logos/paper-plane-sunset.svg',
  },
  {
    slug:        'paper-plane-ocean',
    name:        'Plane (Ocean)',
    description: 'White plane + ring on a deep blue→navy gradient. Cool, coastal, long-haul travel.',
    tags:        ['plane', 'ocean', 'blue', 'coastal', 'circle', 'paper-plane-family'],
    lightUrl:    '/logos/paper-plane-ocean.svg',
    darkUrl:     '/logos/paper-plane-ocean.svg',
    faviconUrl:  '/logos/paper-plane-ocean.svg',
  },
  {
    slug:        'paper-plane-brand',
    name:        'Plane (Brand)',
    description: 'Pink plane on indigo→purple with a cream ring. Closest match to the DiscoveryGem brand palette.',
    tags:        ['plane', 'brand', 'pink', 'discoverygem', 'circle', 'paper-plane-family'],
    lightUrl:    '/logos/paper-plane-brand.svg',
    darkUrl:     '/logos/paper-plane-brand.svg',
    faviconUrl:  '/logos/paper-plane-brand.svg',
  },
];
